import * as React from 'react'
import styled from 'styled-components'

import {CardsCarousel} from '@/components/cards-carousel'
import Image from '@/components/image'
import PlayButton from '@/components/play-button'
import PopoverVideo from '@/components/popover-video'
import type {Props as PrismicLinkProps} from '@/components/prismic-link'
import PrismicLink from '@/components/prismic-link'
import {Text, TextLink} from '@/components/text'
import {useGlobalStrings} from '@/providers/global-strings'
import {Container, Section} from '@/styles/grid'
import {colors, tokens} from '@/theme'
import type {ImageSource} from '@/types/image'
import type {BackgroundColor} from '@/utils/is-valid-background-color'
import type {FontColor} from '@/utils/is-valid-font-color'
import mq from '@/utils/media-query'

interface Props {
    sliderHeading?: string
    highlightFirstCard?: boolean
    hideMediaOnMobile?: boolean
    cards: CardType[]
    backgroundColor?: string
}

interface CardType {
    itemHeading?: string
    subHeading?: string
    description?: string
    subDescription?: string
    image?: ImageSource
    ctaText?: string
    ctaLink?: PrismicLinkProps
    videoId?: string
}

interface CardProps extends CardType {
    hideMediaOnMobile: boolean
    isHighlighted: boolean
    backgroundColor?: BackgroundColor
}

export const SliderCards = ({
    sliderHeading,
    highlightFirstCard = false,
    hideMediaOnMobile = false,
    backgroundColor,
    cards,
}: Props) => {
    return (
        <Section edgeToEdge>
            <Container>
                <Text $variant="heading-base">{sliderHeading}</Text>
            </Container>

            <StyledCardsCarousel>
                {cards.map((card, index) => (
                    <Card
                        key={index}
                        {...card}
                        isHighlighted={highlightFirstCard && index === 0}
                        hideMediaOnMobile={hideMediaOnMobile}
                        backgroundColor={backgroundColor as CardProps['backgroundColor']}
                    />
                ))}
            </StyledCardsCarousel>
        </Section>
    )
}

const StyledCardsCarousel = styled(CardsCarousel)`
    margin-top: ${tokens.spacing40};

    ${mq.min('xs')} {
        grid-auto-columns: 500px;
    }
`

const Card = ({
    itemHeading,
    subHeading,
    description,
    subDescription,
    image,
    ctaText,
    ctaLink,
    videoId,
    isHighlighted,
    hideMediaOnMobile,
    backgroundColor,
}: CardProps) => {
    const globalStrings = useGlobalStrings()
    const hasLowerWrapperContent = (!!ctaLink && !!ctaText) || subHeading || subDescription

    return (
        <StyledCard
            $isHighlighted={isHighlighted}
            $hideOnMobile={hideMediaOnMobile}
            $backgrounColor={backgroundColor}
        >
            <TextWrapper>
                <UpperWrapper>
                    {itemHeading && <Text $variant="title-base">{itemHeading}</Text>}
                    {description && <Text>{description}</Text>}
                </UpperWrapper>

                {hasLowerWrapperContent && (
                    <div>
                        {!!ctaLink && !!ctaText && (
                            <StyledLink
                                as={PrismicLink}
                                $linkColor={
                                    isHighlighted ? colors.text.inverted : colors.text.default
                                }
                                {...ctaLink}
                            >
                                {ctaText} →
                            </StyledLink>
                        )}
                        {subHeading && (
                            <Text
                                $variant="paragraph-large-medium"
                                $color={isHighlighted ? 'inverted' : 'muted'}
                            >
                                {subHeading}
                            </Text>
                        )}
                        {subDescription && (
                            <Text
                                $variant="paragraph-large"
                                $color={isHighlighted ? 'inverted' : 'muted'}
                            >
                                {subDescription}
                            </Text>
                        )}
                    </div>
                )}
            </TextWrapper>

            {image?.url && (
                <MediaWrapper $hideOnMobile={hideMediaOnMobile}>
                    <StyledImage image={image} width={500} />
                    {videoId && (
                        <PopoverVideo videoId={videoId}>
                            {({openPopover}) => (
                                <StyledPlayButton
                                    onClick={openPopover}
                                    aria-label={globalStrings.play}
                                />
                            )}
                        </PopoverVideo>
                    )}
                </MediaWrapper>
            )}
        </StyledCard>
    )
}

interface MediaWrapperProps {
    $hideOnMobile: boolean
}

interface StyledCardProps {
    $isHighlighted: boolean
    $hideOnMobile: boolean
    $backgrounColor?: CardProps['backgroundColor']
}

interface StyledLinkProps {
    $linkColor: (typeof colors.text)[FontColor]
}

const MOBILE_BREAKPOINT = 'xs'

const MediaWrapper = styled.div<MediaWrapperProps>`
    position: relative;
    display: ${(props) => (props.$hideOnMobile ? 'none' : 'block')};

    ${mq.min(MOBILE_BREAKPOINT)} {
        display: block;
    }
`

const StyledPlayButton = styled(PlayButton)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`

const StyledCard = styled.div<StyledCardProps>`
    height: 100%;
    display: grid;
    grid-template-rows: ${(props) => (props.$hideOnMobile ? '1fr auto' : '1fr 150px')};
    align-content: space-between;
    padding: ${tokens.spacing28};
    color: ${(props) => (props.$isHighlighted ? colors.text.inverted : colors.text.default)};
    list-style: none;
    background-color: ${(props) =>
        props.$isHighlighted
            ? colors.background.darkGrey
            : colors.background[props.$backgrounColor || 'default']};
    border: 1px solid
        ${(props) =>
            props.$backgrounColor ? colors.background[props.$backgrounColor] : colors.border.dark};
    row-gap: ${tokens.spacing12};

    ${mq.min(MOBILE_BREAKPOINT)} {
        grid-template-rows: 1fr 240px;
        row-gap: ${tokens.spacing60};
    }
`

const StyledImage = styled(Image)`
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
`

const StyledLink = styled(TextLink)<StyledLinkProps>`
    justify-self: start;
    color: ${(props) => props.$linkColor};
`

const UpperWrapper = styled.div`
    display: grid;
    grid-auto-flow: row;
    row-gap: ${tokens.spacing12};
    align-self: start;
`

const TextWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing20};
    align-content: space-between;
`
